/* eslint-disable max-len */
import styled from '@emotion/styled';
import { FC } from 'react';
import { IconCancel16, IconCheckCircle20 } from 'components/base/Icon';
import List from 'components/base/List';
import Text from 'components/base/Text';
import { bg_primary, gray_inactive_filled, gray_tertiary, pink600 } from 'styles';
import { numberwithComma } from 'util/number';
import { CategoryFilterItem } from 'util/search_utils';

type SubCategory = {
  id: string;
  name: string;
  multiple: boolean;
  properties: boolean;
  selected: boolean;
  count: number;
};
type CategoryItem = {
  id: string;
  name: string;
  multiple: boolean;
  properties: boolean;
  selected: boolean;
  count: number;
  sub_category_list: SubCategory[];
};
interface Props {
  category?: CategoryItem;
  onRemoveCategory: (id: string) => void;
  onSetCategory: (category_list: CategoryFilterItem[]) => void;
}

/**
 * 검색필터 - 서브카테고리 리스트
 * @component
 */
const SubCategories: FC<Props> = ({ category, onRemoveCategory, onSetCategory }) => {
  if (!category) {
    return null;
  }

  const has_selected_sub_categories = category.sub_category_list.some((i) => i.selected);
  const main_category_name = category.name;

  const sub_category_list = category.sub_category_list.map((item) => ({
    ...item,
    id: `${category.id};${item.id}`,
  }));

  const sub_category_list_with_main_category: SubCategory[] = [
    {
      id: category.id,
      name: category.name,
      multiple: false,
      properties: false,
      selected: !has_selected_sub_categories,
      count: category.count,
    },
    ...sub_category_list,
  ];

  const handleResetClick = () => {
    onSetCategory([]);
  };

  const handleSubCategoryClick = (sub_category: SubCategory) => {
    const is_main_category = main_category_name === sub_category.name;

    if (!is_main_category) {
      if (sub_category.selected) {
        const is_last_selected_sub_category = category.sub_category_list.filter((item) => item.selected).length === 1;

        is_last_selected_sub_category
          ? onSetCategory([{ id: category.id, name: category.name, depth: 1 }])
          : onRemoveCategory(sub_category.id);
      } else {
        onSetCategory([
          ...sub_category_list.filter((item) => item.selected).map(({ id, name }) => ({ id, name, depth: 2 })),
          { id: sub_category.id, name: sub_category.name, depth: 2 },
        ]);
      }

      return;
    }

    // MEMO: 메인카테고리 선택 한 경우 서브카테고리는 전부 삭제 후 메인카테고리 1개로 선택변경 필요.
    if (!sub_category.selected) {
      onSetCategory([{ id: sub_category.id, name: sub_category.name, depth: 1 }]);
      return;
    }
  };

  return (
    <div>
      <SC.MainCategory>
        <Text variant='title5_medium' color='gray_secondary'>
          {main_category_name}
        </Text>
        <button className='category_cancel' onClick={handleResetClick}>
          <Text variant='body4_regular' color='gray_secondary'>
            {main_category_name} 선택 취소
          </Text>
          <IconCancel16 color={gray_tertiary} />
        </button>
      </SC.MainCategory>
      <List>
        {sub_category_list_with_main_category.map((item, index) => {
          const is_main_category = main_category_name === item.name;
          const category_count = numberwithComma(item.count, { max_count: 9_999, max_count_subfix: '+' });

          return (
            <List.Item
              left={<IconCheckCircle20 color={item.selected ? pink600 : gray_inactive_filled} />}
              key={index}
              title={is_main_category ? `${item.name} 전체` : item.name}
              title_style={
                item.selected
                  ? { variant: 'title5_medium', color: 'gray_active' }
                  : { variant: 'body2', color: 'gray_inactive' }
              }
              sub_title={category_count}
              sub_title_style={{ variant: 'body4_regular', color: 'gray500' }}
              onClick={() => handleSubCategoryClick(item)}
            />
          );
        })}
      </List>
    </div>
  );
};

export default SubCategories;

const SC = {
  MainCategory: styled.div`
    height: 49px;
    padding: 16px 14px;
    margin: 0 -16px;
    display: flex;
    justify-content: space-between;
    background-color: ${bg_primary};

    .category_cancel {
      display: flex;
      align-items: center;

      > svg {
        cursor: pointer;
        margin-left: 6px;
      }
    }
  `,
};
